import { awaitableConfirmationModal } from '@/modules/base/components/modal/_helpers/awaitableConfirmationModal'
import lang from '@/lang'

// The duplicate-upload modal renders its description as HTML (awaitableConfirmationModal uses
// dangerouslySetInnerHTML), so the document name interpolated into it must be escaped.
export const escapeUploadHtml = (value: string = '') => `${value}`.replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;',
}[char] as string))

// Duplicate prompts must never overlap. processUploads (redux/ducks/document/uploadHelpers) runs
// SIMULTANEOUS_UPLOADS_COUNT (5) uploads at once, so dropping a batch of already-known documents
// asks about each of them at the same moment. Every modal paints its own Dialog backdrop
// (rgba(0,0,0,.5)), and five of those compound to ~97% black: the application vanishes behind the
// stack and only the topmost modal can be answered. Chain the prompts — one document at a time.
let duplicatePromptChain: Promise<void> = Promise.resolve()

// F15.2 — visible to the batch stall detection (uploadHelpers): while a prompt is open the batch
// is deliberately waiting for the USER, and timing it out as "stalled" would reject a batch that
// is working exactly as designed. Counts prompts rather than a boolean so overlapping chained
// prompts keep the signal up until the LAST one is answered.
let openDuplicatePromptCount = 0
export const hasOpenDuplicateUploadPrompt = (): boolean => openDuplicatePromptCount > 0

export const askDuplicateUploadResolution = (documentName: string): Promise<boolean> => {
  const answer = duplicatePromptChain.then(() => awaitableConfirmationModal({
    title: lang.documents.duplicateChecksumModalTitle,
    description: lang.documents.duplicateChecksumModalDesc(escapeUploadHtml(documentName)),
    confirmLabel: lang.documents.useExistingDocument,
    cancelLabel: lang.documents.uploadDuplicateDocument,
    // A dismiss (X / Escape / backdrop) resolves false, which prepareUpload reads as the
    // deliberate "upload anyway" (forceDuplicate). Force an explicit button choice so an
    // accidental dismiss can never silently upload the duplicate the checksum warned about.
    showCloseIcon: false,
    disableEscapeKeyDown: true,
    disableBackdropClick: true,
  }))
  // The chain must survive a dismissed/rejected prompt, or every later duplicate would be skipped.
  duplicatePromptChain = answer.then(() => undefined, () => undefined)
  // Queued prompts count from the ASK, not from when their modal surfaces: the batch is waiting
  // on the user for the whole chained sequence.
  openDuplicatePromptCount += 1
  answer.then(() => { openDuplicatePromptCount -= 1 }, () => { openDuplicatePromptCount -= 1 })
  return answer
}
